import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Users, MapPin, Clock, Award, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { HeroSlider } from '@/components/HeroSlider';

export const HomePage: React.FC = () => {
  const BASE_URL = import.meta.env.BASE_URL;
  // Hero slides with titles and subtitles
  const heroSlides = [
    {
      id: 1,
      image: `${BASE_URL}images/blood_donation_1.jpg.jpg`,
      title: 'Donate Blood, Save Lives',
      subtitle: 'Your single donation can save up to three lives. Join our community of life-savers today.'
    },
    {
      id: 2,
      image: `${BASE_URL}images/blood_samples_2.jpg.jpg`,
      title: 'Safe & Hygienic Process',
      subtitle: 'We follow strict safety protocols to ensure a clean and comfortable donation experience.'
    },
    {
      id: 3,
      image: `${BASE_URL}images/lab_equipment_3.jpg.jpg`,
      title: 'Advanced Testing',
      subtitle: 'Every donation undergoes rigorous testing to ensure the highest safety standards.',
      objectPosition: '50% 30%'  // Adjust this to position the people in the center
    },
    {
      id: 4,
      image: `${BASE_URL}images/clinic_scene_4.jpg.jpg`,
      title: 'Expert Care',
      subtitle: 'Our professional medical staff ensures your donation process is safe and comfortable.'
    },
    {
      id: 5,
      image: `${BASE_URL}images/blood_donation_5.jpg.JPG`,
      title: 'Be a Hero',
      subtitle: 'Your blood donation makes you a hero to someone in need. Schedule your donation today.'
    }
  ];

  const shuffled = [...heroSlides]
    .map((slide) => ({ ...slide, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ sort, ...rest }) => rest);

  return (
    <div className="min-h-screen">
      {/* Hero Section with Slider */}
      <HeroSlider slides={shuffled} />

      {/* Mission Statement */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <h2 className="text-title font-bold text-neutral-900 mb-6">
                Our Mission: Ensuring Safe Blood for Every Need
              </h2>
              <p className="text-body mb-6 text-neutral-700">
                At Sri Balaji Blood Center, we are dedicated to maintaining a safe, adequate blood supply for our community. We serve hospitals, trauma centers, and healthcare facilities with the highest standards of safety and quality.
              </p>
              <p className="text-body mb-8 text-neutral-700">
                Our comprehensive blood banking services include donor screening, blood collection, testing, processing, and distribution. We work tirelessly to ensure that life-saving blood products are available when and where they are needed most.
              </p>
            </div>
            <div className="lg:col-span-5">
              <img 
                src={`${BASE_URL}images/medical_theme_1_0.png`} 
                alt="Medical professionals at work" 
                className="rounded-lg shadow-lg w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

{/* Why Donate Blood */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-title font-bold text-neutral-900 mb-4">Why Donate Blood?</h2>
            <p className="text-body-lg text-neutral-600">Discover the many benefits of blood donation for both donors and recipients</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card hover className="text-center">
              <CardContent>
                <img 
                  src={`${BASE_URL}images/benefits_icons_1_0.jpg`} 
                  alt="Health benefits" 
                  className="w-20 h-20 mx-auto mb-6 rounded-lg"
                />
                <CardTitle>Health Benefits</CardTitle>
                <p className="text-body text-neutral-600">
                  Regular blood donation may help improve cardiovascular health and reduce the risk of certain diseases.
                </p>
              </CardContent>
            </Card>
            <Card hover className="text-center">
              <CardContent>
                <img 
                  src={`${BASE_URL}images/benefits_icons_2_0.jpg`} 
                  alt="Community impact" 
                  className="w-20 h-20 mx-auto mb-6 rounded-lg"
                />
                <CardTitle>Community Impact</CardTitle>
                <p className="text-body text-neutral-600">
                  Your donation directly helps patients in your community, including trauma victims and cancer patients.
                </p>
              </CardContent>
            </Card>
            <Card hover className="text-center">
              <CardContent>
                <img 
                  src={`${BASE_URL}images/benefits_icons_2_4.jpg`} 
                  alt="Personal satisfaction" 
                  className="w-20 h-20 mx-auto mb-6 rounded-lg"
                />
                <CardTitle>Personal Satisfaction</CardTitle>
                <p className="text-body text-neutral-600">
                  Experience the fulfillment that comes from knowing you've made a life-saving contribution.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Urgent Needs */}
      <section className="py-16 bg-primary-50 border-l-4 border-primary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="mb-4 md:mb-0">
              <h3 className="text-subtitle font-semibold text-primary-900 mb-2">Your blood today, someone’s life tomorrow</h3>
              <p className="text-body text-primary-700">
                Your donation takes minutes, but its impact can last a lifetime.
              </p>
            </div>
            <Link to="/contact">
              <Button size="lg">
                <Heart className="mr-2 h-5 w-5" />
                Donate Now
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-title font-bold text-neutral-900 mb-4">How Blood Donation Works</h2>
            <p className="text-body-lg text-neutral-600">Simple steps to become a life-saving hero</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="text-center relative">
              <CardContent>
                <div className="w-12 h-12 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  1
                </div>
                <img 
                  src={`${BASE_URL}images/donation_process_1_2.jpg`} 
                  alt="Registration" 
                  className="w-full h-40 object-cover rounded-lg mb-4"
                />
                <CardTitle>Registration</CardTitle>
                <p className="text-body text-neutral-600">
                  Sign up online or visit our center. Complete a brief health questionnaire.
                </p>
              </CardContent>
            </Card>
            <Card className="text-center relative">
              <CardContent>
                <div className="w-12 h-12 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  2
                </div>
                <img 
                  src={`${BASE_URL}images/donation_process_1_4.jpg`} 
                  alt="Screening" 
                  className="w-full h-40 object-cover rounded-lg mb-4"
                />
                <CardTitle>Screening</CardTitle>
                <p className="text-body text-neutral-600">
                  Quick health check including temperature, pulse, and hemoglobin testing.
                </p>
              </CardContent>
            </Card>
            <Card className="text-center relative">
              <CardContent>
                <div className="w-12 h-12 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  3
                </div>
                <img 
                  src={`${BASE_URL}images/donation_process_2_2.jpg`} 
                  alt="Donation" 
                  className="w-full h-40 object-cover rounded-lg mb-4"
                />
                <CardTitle>Donation</CardTitle>
                <p className="text-body text-neutral-600">
                  Comfortable donation process that takes about 8-10 minutes.
                </p>
              </CardContent>
            </Card>
            <Card className="text-center relative">
              <CardContent>
                <div className="w-12 h-12 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  4
                </div>
                <div className="w-full h-40 bg-green-100 rounded-lg mb-4 flex items-center justify-center">
                  <CheckCircle className="h-16 w-16 text-green-600" />
                </div>
                <CardTitle>Recovery</CardTitle>
                <p className="text-body text-neutral-600">
                  Rest and refreshments for 15 minutes before heading home.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials - Commented out as per request
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-title font-bold text-neutral-900 mb-4">What Our Donors Say</h2>
            <p className="text-body-lg text-neutral-600">Real stories from real heroes</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card>
              <CardContent>
                <div className="flex items-start space-x-4">
                  <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
                    <Users className="h-8 w-8 text-primary-600" />
                  </div>
                  <div>
                    <div className="text-subtitle font-semibold text-neutral-900 mb-2">
                      Sarah M.
                    </div>
                    <p className="text-body text-neutral-600 mb-4">
                      "I've been donating for 5 years now. The staff is always professional and caring. 
                      Knowing that my blood can help save lives makes every donation worthwhile."
                    </p>
                    <div className="flex items-center text-primary-600">
                      <Award className="h-4 w-4 mr-1" />
                      <span className="text-small">15 donations</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <div className="flex items-start space-x-4">
                  <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
                    <Users className="h-8 w-8 text-primary-600" />
                  </div>
                  <div>
                    <div className="text-subtitle font-semibold text-neutral-900 mb-2">
                      James T.
                    </div>
                    <p className="text-body text-neutral-600 mb-4">
                      "As a first-time donor, I was nervous, but the team made me feel comfortable. 
                      The process was smooth and I felt proud knowing I could help someone in need."
                    </p>
                    <div className="flex items-center text-primary-600">
                      <Award className="h-4 w-4 mr-1" />
                      <span className="text-small">First-time donor</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      */}

      {/* Call to Action */}
      <section className="py-24 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-title font-bold mb-6">Ready to Make a Difference?</h2>
          <p className="text-body-lg mb-8 text-red-100">
            Join thousands of donors who are making a life-saving impact in our community. 
            Your next donation could be the one that saves a life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/donors" state={{ scrollTo: 'register-to-donate' }}>
              <Button size="lg" className="bg-white text-primary-600 hover:bg-gray-100">
                <Heart className="mr-2 h-5 w-5" />
                Register as Donor
              </Button>
            </Link>
            <Link to="/contact" state={{ scrollTo: 'contact-form' }}>
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-primary-600">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
